import type { CelestialBodyDefinition } from "../types/celestialBody";
import { J2000_JD } from "../astronomy/constants";
import moonsCatalog from "./moonsCatalog.json";

interface MoonCatalogEntry {
  id: string;
  name: string;
  englishName: string;
  parentId: string;
  meanRadiusKm: number;
  semiMajorAxisKm: number;
  eccentricity: number;
  inclinationDeg: number;
  orbitalPeriodDays: number;
}

const PARENT_COLORS: Record<string, string> = {
  earth: "#C0C0C0",
  mars: "#B08060",
  jupiter: "#D0C090",
  saturn: "#E8D8B0",
  uranus: "#A8D0E0",
  neptune: "#8090C0",
};

function hashHue(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % 360;
}

function toMoonDefinition(
  entry: MoonCatalogEntry,
  index: number,
): CelestialBodyDefinition {
  const e = Math.min(0.95, Math.max(0, entry.eccentricity || 0.01));
  const radius = Math.max(0.1, entry.meanRadiusKm || 0.5);
  const period = Math.max(0.01, Math.abs(entry.orbitalPeriodDays) || 1);
  const baseColor = PARENT_COLORS[entry.parentId] ?? "#AAAAAA";

  return {
    id: entry.id,
    name: entry.name,
    englishName: entry.englishName,
    type: "moon",
    parentId: entry.parentId,
    physical: { meanRadiusKm: radius },
    orbit: {
      epochJulianDate: J2000_JD,
      semiMajorAxisKm: Math.max(100, entry.semiMajorAxisKm),
      eccentricity: e,
      inclinationDeg: entry.inclinationDeg ?? 0,
      longitudeAscendingNodeDeg: (index * 37) % 360,
      argumentOfPeriapsisDeg: (index * 53) % 360,
      meanAnomalyAtEpochDeg: (index * 79) % 360,
      orbitalPeriodDays: period,
    },
    rotation: {
      siderealRotationPeriodHours: period * 24,
      axialTiltDeg: 0,
      rotationDirection:
        (entry.inclinationDeg ?? 0) > 90 ? "retrograde" : "prograde",
      epochJulianDate: J2000_JD,
    },
    color: `hsl(${hashHue(entry.id)}, 28%, ${entry.parentId === "saturn" ? 72 : 62}%)`,
    description: `Lua de ${entry.parentId} (${baseColor})`,
    enabledByDefault: true,
    renderPriority: 100 + index,
  };
}

const catalogEntries = moonsCatalog as MoonCatalogEntry[];

/** Todas as luas planetárias conhecidas no catálogo (Terra→Netuno). */
export const moons: CelestialBodyDefinition[] = catalogEntries.map(
  (entry, index) => toMoonDefinition(entry, index),
);

export const moon = moons.find((m) => m.id === "moon")!;
